import {
  addGlobalErrorInstrumentationHandler,
  type Integration,
} from '@sentry/core';
import type { BrowserOptions } from '@sentry/react';
import {
  addIntegration,
  breadcrumbsIntegration,
  browserProfilingIntegration,
  globalHandlersIntegration,
  init,
  reactRouterV6BrowserTracingIntegration,
  replayIntegration,
  thirdPartyErrorFilterIntegration,
  withProfiler,
  wrapCreateBrowserRouterV6,
} from '@sentry/react';
import { useEffect } from 'react';
import {
  createBrowserRouter as RRcreateBrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router';

import { API_URL } from '#src/config.ts';
import { sendSafeError } from '#src/lib/error.ts';
import { scheduleIdle } from '#src/lib/scheduleIdle.ts';

// window.onerror gets the thrown value as its error argument, so a bare
// `throw undefined` (R3F's internal event dispatch) yields no error object and
// no stack — only the browser's generic message. Handlers we control are
// wrapped via safeHandler (trois/src/lib/r3f.ts).
const MESSAGELESS_THROWS = new Set([
  'uncaught undefined', // Chrome/Safari
  'uncaught exception: undefined', // Firefox
  'undefined',
  'uncaught', // a DOMException thrown by a third party, with no message of its own
]);
const isMessagelessThrow = (handlerData: {
  error?: unknown;
  msg: string | object;
}): boolean =>
  typeof handlerData.msg === 'string' &&
  MESSAGELESS_THROWS.has(handlerData.msg.trim().toLowerCase());

const customGlobalHandlersIntegration = () => {
  return {
    name: 'customGlobalHandlersIntegration',
    setup() {
      addGlobalErrorInstrumentationHandler(handlerData => {
        if (isMessagelessThrow(handlerData)) {
          return;
        }
        sendSafeError(handlerData);
      });
    },
  };
};

// See https://gist.github.com/impressiver/5092952
const communityIgnoredErrors = [
  // Random plugins/extensions
  'top.GLOBALS',
  // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
  'originalCreateNotification',
  'canvas.contentDocument',
  'MyApp_RemoveAllHighlights',
  'http://tt.epicplay.com',
  "Can't find variable: ZiteReader",
  'jigsaw is not defined',
  'ComboSearch is not defined',
  'http://loading.retry.widdit.com/',
  'atomicFindClose',
  // Facebook borked
  'fb_xd_fragment',
  // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to reduce this. (thanks @acdha)
  // See http://stackoverflow.com/questions/4113268/how-to-stop-javascript-injection-from-vodafone-proxy
  'bmi_SafeAddOnload',
  'EBCallBackMessageReceived',
  // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
  'conduitPage',
  // Generic error code from errors outside the security sandbox
  // You can delete this if using raven.js > 1.0, which ignores these automatically.
  'Script error.',
  // Those are actual errors which are raised whenever there is a recaptcha error
  // there is nothing to do for those
  'Invalid recaptcha token',
];

const customIgnoredErrors = [
  // Ignoring this because there's nothing that can be done when this is thrown, and currently
  // thrown by launchdarkly API
  'A network error occurred',

  // Ignore network failed errors as they are not actionable & already handled with an error page
  'TypeError: Failed to fetch', // Chrome
  'TypeError: Importing a module script failed', // Safari
  'TypeError: NetworkError when attempting to fetch resource', // Firefox
  'Error: Load failed', // iOS
  // v3 wrapped transport failures in ApolloError, so this arrived as
  // "ApolloError: timeout". v4 throws the underlying error directly, so match on
  // the message whatever class now carries it.
  /: timeout$/,

  // TODO(SO5-952): Remove these once we can actually solve it, but for now they're spamming us
  "Cannot read properties of undefined (reading 'focus')",
  /is not an object \(evaluating '\w+\.input\.focus'\)/,

  // Spamming errors about 3D assets, the canvas falls back on its own
  /Could not load .*\/assets\//,
  /Could not load.*?.gltf/,

  // skip rate limit errors
  /rate limit exceeded/,
  /Too many requests sent in the same time/,
  'Received status code 429',

  // a deploy removed the chunk this tab was still pointing at, a reload fixes it
  'error loading dynamically imported module',
  /Importing module .* is not found/,

  // Android WebView tears the JS bridge down before the page stops using it
  /Error invoking \w+: Java object is gone/,

  // third-party iframes we cannot reach into
  'Blocked a frame with origin',

  // Ignore errors from API calls done while being logged out
  /Not authorized to access/,

  // There is a fallback for such a case. webgl can be disabled for security/privacy reasons.
  'Error creating WebGL context with your selected attributes.',

  // Especially on mobile, we can't do anything about this
  'Large Render Blocking Asset',

  // Not in our control: https://github.com/getsentry/sentry-javascript/issues/8444
  /logMutedMessage/,

  /*
   * This is a known issue with sentry wrapping addEventListner API on iOS devices
   * https://github.com/getsentry/sentry-javascript/issues/3040
   */
  /undefined is not an object \(evaluating 'window.webkit./,
];

const ignoredErrorPatterns = [
  ...communityIgnoredErrors,
  ...customIgnoredErrors,
];

const ignoredUrls = [
  // Facebook flakiness
  /graph\.facebook\.com/i,
  // Facebook blocked
  /connect\.facebook\.net\/en_US\/all\.js/i,
  // Woopra flakiness
  /eatdifferent\.com\.woopra-ns\.com/i,
  /static\.woopra\.com\/js\/woopra\.js/i,
  // Chrome extensions
  /extensions\//i,
  /^chrome:\/\//i,
  // Other plugins
  /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
  /webappstoolbarba\.texthelp\.com\//i,
  /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
];

type SentryApp = 'sorare' | 'playground' | 'rendering';

export const startSentry = ({
  dsn,
  env,
  release,
}: {
  dsn: string;
  env: string;
  release: string;
  app: SentryApp;
}): { createBrowserRouter: typeof RRcreateBrowserRouter } => {
  const sentryEnv = env === 'production' ? 'prod' : env;
  const isDevelopment = sentryEnv === 'development';

  let sampleRates: {
    traces: number;
    replaysOnError: number;
    profiles: number;
  };

  switch (sentryEnv) {
    case 'prod':
      sampleRates = {
        traces: 0.01,
        profiles: 1,
        replaysOnError: 0.01,
      };
      break;
    case 'staging':
    case 'mockprod':
      sampleRates = {
        traces: 0.01,
        profiles: 1,
        replaysOnError: 1,
      };
      break;
    default:
      sampleRates = {
        traces: 0.01,
        profiles: 1,
        replaysOnError: 0,
      };
      break;
  }

  // Integrations that must be installed synchronously to instrument the
  // boot critical path (breadcrumbs, tracing, error handlers, filters).
  // Profiling and error-tied replay are added later via `scheduleIdle`
  // below
  const integrations: Integration[] = [
    globalHandlersIntegration({
      onerror: false,
      onunhandledrejection: false,
    }),
    customGlobalHandlersIntegration(),
    breadcrumbsIntegration({
      console: !isDevelopment,
    }),
    reactRouterV6BrowserTracingIntegration({
      instrumentNavigation: false,
      stripBasename: true,
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    thirdPartyErrorFilterIntegration({
      filterKeys: ['sorare-vite-application'],
      behaviour: 'apply-tag-if-contains-third-party-frames',
    }),
  ];

  const options: BrowserOptions = {
    dsn,
    environment: sentryEnv,
    allowUrls: isDevelopment ? [] : ['/static/js', '/assets'],
    denyUrls: ignoredUrls,
    ignoreErrors: ignoredErrorPatterns,
    integrations,
    tracesSampleRate: sampleRates.traces,
    tracePropagationTargets: [API_URL],
    release,
    replaysOnErrorSampleRate: sampleRates.replaysOnError,
    // oxlint-disable-next-line typescript/no-deprecated -- migrating to profileSessionSampleRate/profileLifecycle changes what gets sampled; needs its own change
    profilesSampleRate: sampleRates.profiles,
  };
  init(options);
  // Must be after init
  const createBrowserRouter = wrapCreateBrowserRouterV6(RRcreateBrowserRouter);

  scheduleIdle(() => {
    try {
      addIntegration(browserProfilingIntegration());
      addIntegration(replayIntegration());
    } catch {}
  });

  return { createBrowserRouter };
};

export { withProfiler };
